import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query'

import type { Page, Post } from '@/api/types'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT } from '@/i18n'

import { PostCard } from './PostCard'

interface Props {
  query: UseInfiniteQueryResult<InfiniteData<Page<Post>>, Error>
  emptyTitle: string
  emptyDescription?: string
  onRequireLogin?: () => void
  /** 用户页的置顶帖（D69）：单独放在最上面，列表里服务端已经排除了它 */
  pinned?: Post | null
}

/** 无限滚动的帖子列表：骨架 → 内容 → 哨兵 → 到底提示。 */
export function PostList({
  query,
  emptyTitle,
  emptyDescription,
  onRequireLogin,
  pinned = null,
}: Props) {
  const t = useT()
  const {
    data,
    isPending,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = query

  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
  })

  if (isPending) return <PostListSkeleton />
  if (isError)
    return (
      <ErrorState message={error?.message ?? t('error.loadFailed')} onRetry={() => refetch()} />
    )

  const posts = data?.pages.flatMap((page) => page.items) ?? []
  if (posts.length === 0 && !pinned)
    return <EmptyState title={emptyTitle} description={emptyDescription} />

  return (
    <div>
      {pinned && <PostCard post={pinned} pinned onRequireLogin={onRequireLogin} />}
      {posts.map((post) => (
        <PostCard key={`${post.id}`} post={post} onRequireLogin={onRequireLogin} />
      ))}

      <div ref={sentinelRef} aria-hidden className="h-px" />
      {isFetchingNextPage && <CenteredSpinner />}
      {!hasNextPage && (posts.length > 0 || pinned) && (
        <p className="py-8 text-center text-[15px] text-ink-muted">{t('timeline.noMore')}</p>
      )}
    </div>
  )
}
